import type { ApolloQueryResult } from '@apollo/client';
import { Form, Formik } from 'formik';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { object, string } from 'yup';

import { FormikTextField } from '@components/FormikFields';
import RoundedButton from '@components/RoundedButton/index';

import { isDevelopment } from '@constants/env';

import { useUpdateUserName } from '@hooks/api/user';

import type { GetUserInput, UserModel } from 'generated/types';
import type { GetUserRecordQuery } from 'generated/user.generated';

const messages = defineMessages({
  firstName: {
    id: 'GLOBAL.firstName',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'GLOBAL.lastName',
    defaultMessage: 'Last Name',
  },
  save: {
    id: 'GLOBAL.save',
    defaultMessage: 'Save',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  middleName: {
    id: 'GLOBAL.middleName',
    defaultMessage: 'Middle Name',
  },
  updateProfile: {
    id: 'changeName.updateProfile',
    defaultMessage: 'Update Profile',
  },
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
});

type FormValues = {
  first_name: string;
  middle_name?: string;
  last_name: string;
};

interface ChangeNameProps {
  refetch: (
    variables?:
      | {
          input: GetUserInput;
        }
      | undefined
  ) => Promise<ApolloQueryResult<GetUserRecordQuery>>;
  initialLoading: boolean;
  user?: Pick<
    UserModel,
    'first_name' | 'last_name' | 'middle_name' | 'user_id'
  >;
}
const ChangeName: React.FC<ChangeNameProps> = ({
  user,
  refetch,
  initialLoading,
}) => {
  const { formatMessage } = useIntl();

  const [updateName, updateNameLoading] = useUpdateUserName();

  const loading = updateNameLoading || initialLoading;

  const validationSchema = object().shape({
    first_name: string().required(formatMessage(messages.required)),
    last_name: string().required(formatMessage(messages.required)),
    middle_name: string(),
  });

  async function handleSubmit({
    first_name,
    middle_name,
    last_name,
  }: FormValues) {
    if (!user) {
      return;
    }
    const { user_id: id } = user;
    try {
      await updateName({
        input: {
          user_id: id,
          first_name,
          middle_name,
          last_name,
        },
      });
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    } finally {
      await refetch({
        input: { user_id: id },
      });
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        first_name: user?.first_name ?? '',
        middle_name: user?.middle_name ?? '',
        last_name: user?.last_name ?? '',
      }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ dirty, isSubmitting }) => (
        <Form>
          <FormikTextField
            autoComplete="off"
            charLimit={50}
            name="first_name"
            label={formatMessage(messages.firstName)}
            required
          />
          <FormikTextField
            autoComplete="off"
            charLimit={50}
            name="middle_name"
            label={formatMessage(messages.middleName)}
          />
          <FormikTextField
            autoComplete="off"
            charLimit={50}
            name="last_name"
            label={formatMessage(messages.lastName)}
            required
          />

          <RoundedButton
            disabled={isSubmitting || !dirty || loading}
            type="submit"
          >
            {formatMessage(messages.save)}
          </RoundedButton>
        </Form>
      )}
    </Formik>
  );
};

export default ChangeName;
